/**
 * AlertsContext - Contexto global para el sistema de alertas de precio
 * Maneja la creación, eliminación y verificación de alertas
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, TrendDown, Bell } from '@phosphor-icons/react';
import { playAlertSound, initAudioContext } from '@/utils/AlertSound';
import { logger } from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsContext = createContext(null);

export const useAlerts = () => {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts debe usarse dentro de AlertsProvider');
  }
  return context;
};

export const AlertsProvider = ({ children, markets: initialMarkets = [] }) => {
  const [alerts, setAlerts] = useState([]);
  const [markets, setMarkets] = useState(initialMarkets);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('alertSoundEnabled');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  const triggeredRef = useRef(new Set());
  const checkIntervalRef = useRef(null);

  // Actualizar markets cuando cambien desde props
  useEffect(() => {
    if (initialMarkets && initialMarkets.length > 0) {
      setMarkets(initialMarkets);
    }
  }, [initialMarkets]);

  // Método para actualizar markets desde fuera
  const updateMarkets = useCallback((newMarkets) => {
    setMarkets(newMarkets);
  }, []);

  // Guardar configuración de sonido
  useEffect(() => {
    localStorage.setItem('alertSoundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  // Cargar alertas del backend
  const fetchAlerts = useCallback(async () => {
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        logger.info('No token found, skipping alerts fetch');
        setAlerts([]);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Usar el nuevo endpoint de usuario autenticado
      const response = await axios.get(`${API}/user/alerts`, { headers });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      logger.error('Error fetching alerts:', error);
      // Intentar cargar desde localStorage como fallback
      const localAlerts = localStorage.getItem('priceAlerts');
      if (localAlerts) {
        try {
          const parsed = JSON.parse(localAlerts);
          setAlerts(parsed);
          logger.info('Loaded', parsed.length, 'alerts from localStorage');
        } catch (e) {
          logger.error('Error parsing local alerts:', e);
        }
      }
    }
  }, []);

  // Guardar alertas en localStorage como backup (solo si el usuario tiene permisos)
  useEffect(() => {
    let userData = {};
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (userDataStr) userData = JSON.parse(userDataStr);
    } catch (e) {
      console.warn('Error parsing user_data:', e.message);
    }
    const plan = userData.plan || 'free';
    // Solo guardar en localStorage si el usuario tiene plan con alertas
    if (alerts.length > 0 && plan !== 'free') {
      localStorage.setItem('priceAlerts', JSON.stringify(alerts));
    }
  }, [alerts]);

  // Cargar alertas al iniciar y cuando cambia el usuario
  useEffect(() => {
    // Delay más largo para dar tiempo al auto-login
    const timer = setTimeout(() => {
      fetchAlerts();
    }, 1500);
    return () => clearTimeout(timer);
  }, [fetchAlerts]);
  
  // Escuchar cambios de usuario (login/logout)
  useEffect(() => {
    const handleUserChange = () => {
      console.log('🔄 Usuario cambió, recargando alertas...');
      // Limpiar alertas actuales
      setAlerts([]);
      // Recargar alertas del nuevo usuario
      setTimeout(fetchAlerts, 500);
    };
    
    // Escuchar evento de storage para cambios de usuario
    window.addEventListener('storage', (e) => {
      if (e.key === 'current_user_id') {
        handleUserChange();
      }
    });
    
    return () => {
      window.removeEventListener('storage', handleUserChange);
    };
  }, [fetchAlerts]);

  // Crear nueva alerta
  const createAlert = useCallback(async (ticker, targetPrice, condition = 'above', source = null) => {
    try {
      setLoading(true);
      
      // Intentar guardar en backend
      try {
        await axios.post(`${API}/alerts`, {
          ticker: ticker.toUpperCase(),
          target_price: parseFloat(targetPrice),
          condition,
          source: source || 'manual'
        });
      } catch (backendError) {
        logger.warn('Error saving to backend, saving locally:', backendError);
      }

      // Añadir localmente
      const newAlert = {
        id: `${ticker}-${condition}-${targetPrice}-${Date.now()}`,
        ticker: ticker.toUpperCase(),
        target_price: parseFloat(targetPrice),
        condition,
        created_at: new Date().toISOString(),
        source: source || { name: 'Portafolio', icon: 'Bell', color: 'amber' },
        active: true
      };

      setAlerts(prev => {
        // Evitar duplicados
        const exists = prev.some(a => 
          a.ticker === newAlert.ticker && 
          a.condition === newAlert.condition && 
          Math.abs(a.target_price - newAlert.target_price) < 0.01
        );
        if (exists) {
          toast.warning('Ya existe una alerta similar para este activo');
          return prev;
        }
        return [...prev, newAlert];
      });

      toast.success(
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-[#00ff6a]" />
          <div>
            <div className="font-bold">Alerta creada</div>
            <div className="text-xs opacity-75">
              {ticker} {condition === 'above' ? 'sube a' : 'baja a'} {parseFloat(targetPrice).toFixed(2)}€
            </div>
          </div>
        </div>,
        { duration: 4000 }
      );

      // Inicializar audio context si es la primera alerta
      initAudioContext();

      // Refrescar alertas desde el backend para mantener sincronización
      await fetchAlerts();

      return true;
    } catch (error) {
      logger.error('Error creating alert:', error);
      toast.error('Error al crear la alerta');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAlerts]);

  // Actualizar alerta existente
  const updateAlert = useCallback(async (alertId, updates) => {
    try {
      setLoading(true);
      const alertToUpdate = alerts.find(a => a.id === alertId);
      if (!alertToUpdate) {
        toast.error('Alerta no encontrada');
        return false;
      }

      // Actualizar localmente
      setAlerts(prev => prev.map(a => {
        if (a.id === alertId) {
          // Resetear triggered si cambia el precio o condición
          const oldKey = `${a.ticker}-${a.condition}-${a.target_price}`;
          triggeredRef.current.delete(oldKey);
          
          return {
            ...a,
            ...updates,
            target_price: updates.target_price !== undefined ? parseFloat(updates.target_price) : a.target_price,
            updated_at: new Date().toISOString()
          };
        }
        return a;
      }));

      toast.success(
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-gold-400" />
          <div>
            <div className="font-bold">Alerta actualizada</div>
            <div className="text-xs opacity-75">
              {alertToUpdate.ticker} - Nuevo precio: {parseFloat(updates.target_price || alertToUpdate.target_price).toFixed(2)}€
            </div>
          </div>
        </div>,
        { duration: 3000 }
      );

      return true;
    } catch (error) {
      logger.error('Error updating alert:', error);
      toast.error('Error al actualizar la alerta');
      return false;
    } finally {
      setLoading(false);
    }
  }, [alerts]);

  // Eliminar alerta
  const deleteAlert = useCallback(async (alertId) => {
    try {
      const alert = alerts.find(a => a.id === alertId);
      if (alert) {
        // Intentar eliminar del backend por ID
        try {
          await axios.delete(`${API}/alerts/id/${alertId}`);
        } catch (backendError) {
          logger.warn('Error deleting from backend:', backendError);
        }

        // Eliminar de triggered
        triggeredRef.current.delete(`${alert.ticker}-${alert.condition}-${alert.target_price}`);
      }

      setAlerts(prev => prev.filter(a => a.id !== alertId));
      
      // Refrescar alertas desde el backend para mantener sincronización
      await fetchAlerts();
      
      toast.success('Alerta eliminada');
    } catch (error) {
      logger.error('Error deleting alert:', error);
      toast.error('Error al eliminar la alerta');
    }
  }, [alerts, fetchAlerts]);

  // Obtener alertas de un ticker específico
  const getAlertsForTicker = useCallback((ticker) => {
    return alerts.filter(a => a.ticker === ticker.toUpperCase());
  }, [alerts]);

  // Verificar si un ticker tiene alertas
  const hasAlerts = useCallback((ticker) => {
    return alerts.some(a => a.ticker === ticker.toUpperCase());
  }, [alerts]);

  // Verificar alertas contra precios actuales
  const checkAlerts = useCallback(() => {
    if (!markets || markets.length === 0 || alerts.length === 0) return;

    const priceMap = {};
    markets.forEach(m => {
      if (m.priceEUR > 0) {
        priceMap[m.ticker] = m.priceEUR;
      }
    });

    alerts.forEach(alert => {
      const price = priceMap[alert.ticker];
      if (!price) return;

      const alertKey = `${alert.ticker}-${alert.condition}-${alert.target_price}`;
      if (triggeredRef.current.has(alertKey)) return;

      let triggered = false;

      if (alert.condition === 'above' && price >= alert.target_price) {
        triggered = true;
        if (soundEnabled) playAlertSound('priceUp');
        toast.success(
          <div className="flex items-center gap-2">
            <TrendUp size={20} className="text-[#00ff6a]" />
            <div>
              <div className="font-bold text-lg">{alert.ticker} SUBIÓ</div>
              <div className="text-sm">Alcanzó {alert.target_price.toFixed(2)}€</div>
              <div className="text-xs opacity-75">Precio actual: {price.toFixed(2)}€</div>
            </div>
          </div>,
          { duration: 15000, className: 'alert-toast-success' }
        );
      } else if (alert.condition === 'below' && price <= alert.target_price) {
        triggered = true;
        if (soundEnabled) playAlertSound('priceDown');
        toast.error(
          <div className="flex items-center gap-2">
            <TrendDown size={20} className="text-[#ff4444]" />
            <div>
              <div className="font-bold text-lg">{alert.ticker} BAJÓ</div>
              <div className="text-sm">Llegó a {alert.target_price.toFixed(2)}€</div>
              <div className="text-xs opacity-75">Precio actual: {price.toFixed(2)}€</div>
            </div>
          </div>,
          { duration: 15000, className: 'alert-toast-danger' }
        );
      }

      if (triggered) {
        triggeredRef.current.add(alertKey);
        
        // Llamar al backend para registrar el trigger
        axios.post(`${API}/alerts/${alert.ticker}/trigger?current_price=${price}`).catch(err => {
          logger.warn('Error triggering alert in backend:', err);
        });
      }
    });
  }, [alerts, markets, soundEnabled]);

  // Ejecutar verificación cuando cambien los precios
  useEffect(() => {
    checkAlerts();
  }, [checkAlerts]);

  // También verificar periódicamente
  useEffect(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    
    checkIntervalRef.current = setInterval(checkAlerts, 30000); // Cada 30 segundos

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkAlerts]);

  // Calcular progreso hacia el objetivo
  const getAlertProgress = useCallback((alert, currentPrice) => {
    if (!currentPrice || !alert.target_price) return 0;

    if (alert.condition === 'above') {
      if (currentPrice >= alert.target_price) return 100;
      const diff = alert.target_price - currentPrice;
      const range = alert.target_price * 0.1;
      return Math.max(0, Math.min(100, ((range - diff) / range) * 100));
    } else {
      if (currentPrice <= alert.target_price) return 100;
      const diff = currentPrice - alert.target_price;
      const range = alert.target_price * 0.1;
      return Math.max(0, Math.min(100, ((range - diff) / range) * 100));
    }
  }, []);

  // Test de sonido
  const testSound = useCallback(() => {
    playAlertSound('priceUp');
  }, []);

  const value = {
    alerts,
    loading,
    soundEnabled,
    setSoundEnabled,
    createAlert,
    updateAlert,
    deleteAlert,
    getAlertsForTicker,
    hasAlerts,
    getAlertProgress,
    testSound,
    fetchAlerts,
    updateMarkets,
    totalAlerts: alerts.length
  };

  return (
    <AlertsContext.Provider value={value}>
      {children}
    </AlertsContext.Provider>
  );
};

export default AlertsContext;
